/**
 * URL Utilities for handling authentication tokens in URLs
 * This file provides functions for working with URLs that need authentication
 */
import { useAuthStore } from '@/stores';

// Type definitions
interface RateLimiter {
  tokens: number;
  lastRefill: number;
  capacity: number;
  refillRate: number;
  getToken(): Promise<void>;
}

/**
 * Fetches an image using the secure endpoint that accepts JWT in Authorization header
 * and returns it as a blob URL that can be used in <img> tags.
 * 
 * @param projectId - The ID of the project
 * @param photoId - The ID of the photo to fetch
 * @param maxSizeMB - Maximum size in MB (default: 10)
 * @returns A blob URL that can be used in <img> tags
 */
export async function fetchImageSecurely(
  projectId: string | number, 
  photoId: string | number, 
  maxSizeMB: number = 50
): Promise<string> {
  const fetchImage = async (): Promise<string> => {
    const authStore = useAuthStore()
    const token = authStore.getAccessToken;
    if (!token) {
      throw new Error('No access token available');
    }

    // Input validation
    if (!photoId || (typeof photoId !== 'string' && typeof photoId !== 'number')) {
      throw new Error('Invalid photo ID');
    }

    // Create AbortController for timeout
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 30000); // 30 second timeout

    try {
      // Fetch the image using the secure endpoint with CSRF token
      const response = await fetch(`/api/project/${projectId}/photos/${photoId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          // Allow images and videos (and other binary types) when fetching media
          'Accept': '*/*',
          'X-CSRF-TOKEN': (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content || '',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin', // Include cookies for CSRF
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
      }

      // Security checks
      const contentType = response.headers.get('content-type');
      // We accept image/* and video/* content types here. The caller will decide how to render.
      if (!contentType || (!contentType.startsWith('image/') && !contentType.startsWith('video/'))) {
        // Not strictly fatal — return blob URL anyway but log a warning so developers can inspect
        console.warn(`fetchImageSecurely: unexpected content-type ${contentType} for ${photoId}`);
      }

      // Size validation
      const contentLength = response.headers.get('content-length');
      if (contentLength) {
        const sizeInBytes = parseInt(contentLength, 10);
        const sizeInMB = sizeInBytes / (1024 * 1024);
        if (sizeInMB > maxSizeMB) {
          throw new Error(`Image too large: ${sizeInMB.toFixed(2)}MB (max: ${maxSizeMB}MB)`);
        }
      }

      // Convert response to blob
      const blob = await response.blob();
      
      // Additional size check after download
      const actualSizeInMB = blob.size / (1024 * 1024);
      if (actualSizeInMB > maxSizeMB) {
        throw new Error(`Image too large: ${actualSizeInMB.toFixed(2)}MB (max: ${maxSizeMB}MB)`);
      }

      // Create and return blob URL
      const blobUrl = URL.createObjectURL(blob);
      return blobUrl;

    } finally {
      clearTimeout(timeout);
    }
  };

  // Rate limiting implementation
  const rateLimiter: RateLimiter = {
    tokens: 10,
    lastRefill: Date.now(),
    capacity: 10,
    refillRate: 2, // tokens per second
    
    async getToken(): Promise<void> {
      const now = Date.now();
      const timePassed = (now - this.lastRefill) / 1000;
      this.tokens = Math.min(this.capacity, this.tokens + timePassed * this.refillRate);
      this.lastRefill = now;
      
      if (this.tokens < 1) {
        const waitTime = (1 - this.tokens) / this.refillRate * 1000;
        await new Promise(resolve => setTimeout(resolve, waitTime));
        this.tokens = 1;
      }
      
      this.tokens -= 1;
    }
  };

  // Apply rate limiting
  await rateLimiter.getToken();
  
  // Retry logic with exponential backoff
  let retries = 3;
  while (retries > 0) {
    try {
      return await fetchImage();
    } catch (error) {
      retries--;
      if (retries === 0) throw error;
      
      // Exponential backoff: wait 2^(3-retries) seconds
      const waitTime = Math.pow(2, 3 - retries) * 1000;
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
  }
  
  throw new Error('Failed to fetch image after retries');
}

/**
 * Creates a secure URL for downloading files with authentication
 * @param endpoint - The API endpoint
 * @returns A secure download URL
 */
export function createSecureDownloadUrl(endpoint: string): string {
  const authStore = useAuthStore()
  const token = authStore.getAccessToken;
  if (!token) {
    throw new Error('No access token available for secure download');
  }
  
  const url = new URL(endpoint, window.location.origin);
  url.searchParams.append('access_token', token);
  return url.toString();
}

/**
 * Revokes a blob URL to prevent memory leaks
 * @param blobUrl - The blob URL to revoke
 */
export function revokeBlobUrl(blobUrl: string): void {
  if (blobUrl && blobUrl.startsWith('blob:')) {
    URL.revokeObjectURL(blobUrl);
  }
}

/**
 * Fetches a firm logo using the secure endpoint that accepts JWT in Authorization header
 * and returns it as a blob URL that can be used in <img> tags.
 * 
 * @param firmId - The ID of the firm
 * @param maxSizeMB - Maximum size in MB (default: 10)
 * @returns A blob URL that can be used in <img> tags
 */
export async function fetchFirmLogoSecurely(
  maxSizeMB: number = 10
): Promise<string> {
  const authStore = useAuthStore()
  const token = authStore.getAccessToken;
  if (!token) {
    throw new Error('No access token available');
  }

  // Create AbortController for timeout
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 30000); // 30 second timeout

  try {
    // Fetch the logo using the secure endpoint with CSRF token
    const response = await fetch(`/api/firms/logo`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        // Allow images and other binary types when fetching media
        'Accept': '*/*',
        'X-CSRF-TOKEN': (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content || '',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin', // Include cookies for CSRF
      signal: controller.signal
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch firm logo: ${response.status} ${response.statusText}`);
    }

    // Convert response to blob
    const blob = await response.blob();
    
    // Additional size check after download
    const actualSizeInMB = blob.size / (1024 * 1024);
    if (actualSizeInMB > maxSizeMB) {
      throw new Error(`Logo too large: ${actualSizeInMB.toFixed(2)}MB (max: ${maxSizeMB}MB)`);
    }

    // Create and return blob URL
    const blobUrl = URL.createObjectURL(blob);
    return blobUrl;

  } finally {
    clearTimeout(timeout);
  }
}

/**
 * Fetches an issue photo using the secure endpoint that accepts JWT in Authorization header
 * and returns it as a blob URL that can be used in <img> tags.
 * 
 * @param projectId - The ID of the project
 * @param issueId - The ID of the issue
 * @param photoId - The ID of the photo to fetch
 * @param maxSizeMB - Maximum size in MB (default: 50)
 * @returns A blob URL that can be used in <img> tags
 */
export async function fetchIssuePhotoSecurely(
  projectId: string | number,
  issueId: string | number, 
  photoId: string | number, 
  maxSizeMB: number = 50
): Promise<string> {
  const authStore = useAuthStore()
  const token = authStore.getAccessToken;
  if (!token) {
    throw new Error('No access token available');
  }

  // Input validation
  if (!photoId || (typeof photoId !== 'string' && typeof photoId !== 'number')) {
    throw new Error('Invalid photo ID');
  }

  // Create AbortController for timeout
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 30000); // 30 second timeout

  try {
    // Fetch the photo using the secure endpoint with CSRF token
    const response = await fetch(`/api/project/${projectId}/issues/${issueId}/photos/${photoId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'image/*',
        'X-CSRF-TOKEN': (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content || '',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      signal: controller.signal
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch issue photo: ${response.status} ${response.statusText}`);
    }

    // Security checks
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.startsWith('image/')) {
      console.warn(`fetchIssuePhotoSecurely: unexpected content-type ${contentType} for ${photoId}`);
    }

    // Size validation
    const contentLength = response.headers.get('content-length');
    if (contentLength) {
      const sizeInBytes = parseInt(contentLength, 10);
      const sizeInMB = sizeInBytes / (1024 * 1024);
      if (sizeInMB > maxSizeMB) {
        throw new Error(`Photo too large: ${sizeInMB.toFixed(2)}MB (max: ${maxSizeMB}MB)`);
      }
    }

    // Convert response to blob
    const blob = await response.blob();
    
    // Additional size check after download
    const actualSizeInMB = blob.size / (1024 * 1024);
    if (actualSizeInMB > maxSizeMB) {
      throw new Error(`Photo too large: ${actualSizeInMB.toFixed(2)}MB (max: ${maxSizeMB}MB)`);
    }

    // Create and return blob URL
    const blobUrl = URL.createObjectURL(blob);
    return blobUrl;

  } finally {
    clearTimeout(timeout);
  }
}

/**
 * Validates if a URL is safe for use
 * @param url - The URL to validate
 * @returns Whether the URL is considered safe
 */
export function isUrlSafe(url: string): boolean {
  try {
    const urlObj = new URL(url, window.location.origin);
    
    // Only allow http and https protocols
    if (!['http:', 'https:'].includes(urlObj.protocol)) {
      return false;
    }
    
    // For relative URLs, ensure they're on the same origin
    if (!url.startsWith('http')) {
      return urlObj.origin === window.location.origin;
    }
    
    return true;
  } catch {
    return false;
  }
}
